// MS-6583: gate network-level fetch failures before they reach Bugsnag.
//
// One underlying event — a full-page navigation (the MS-6466 takeover hand-off, a
// redirectToLogin, the min-version reload, the user leaving) aborting the page's in-flight
// fetches, or a flaky connection dropping them — used to cost 2-3 Bugsnag events, because
// every container's catch notifies its own rejection and the two call styles split into two
// error groups (the raw TypeError "Failed to fetch" vs execServerRequest's Error-wrapped
// copy). ~1,200 events/day, none of them actionable: the failing request never names itself
// (no URL survives into the error), and the user is either already navigating away or
// already offline.
//
// This is ONE chokepoint rather than 300 catch-site edits — and it must be a wrapper around
// Bugsnag.notifyException, NOT a beforeNotify hook: the vendored v3.0.6 notifier spends its
// 10-events-per-page allowance BEFORE beforeNotify runs (`O-=1` at the top of A()), so a
// cancelled burst would still mute every later, actionable error on that page. The wrapper
// drops noise before the notifier sees it, leaving the allowance intact. Everything that is
// not a network-level failure passes through untouched. A network failure is dropped when
// the page can explain it (hand-off navigation, unload, hidden tab, offline) and
// rate-limited to one annotated event per window otherwise — so a real outage
// (CORS/DNS/deploy breakage) still surfaces, once, with the context needed to read it.
//
// The allowed event carries what the dropped ones cannot: a per-reason count of the noise
// suppressed since the last one, and — when fetchM attached them (networkDiagnostics.js) —
// the failing request's URL and the page state at the moment it died. That is how the
// unexplained share of MS-6583 gets attributed without a single extra event.
import { isSessionHandoffInProgress } from './session';

// EXACT network-level rejection messages, by source: Chrome "Failed to fetch", Firefox
// "NetworkError when attempting to fetch resource.", Safari "Load failed", and the
// whatwg-fetch polyfill's "Network request failed" (active wherever native fetch is absent —
// book.html deletes window.fetch on Edge). An optional single "TypeError: " prefix covers
// the legacy execServerRequest wrapping still in old bundles. Anchored on purpose: a
// substring match would swallow real errors that merely contain "load failed".
const NETWORK_MESSAGE_RE = /^(?:typeerror:\s*)?(?:failed to fetch|networkerror when attempting to fetch resource\.?|load failed|network request failed)$/i;

// One annotated event per window keeps the canary for a genuine outage without the burst:
// a dead backend fails every page load, so one event per window per user is still a clear
// daily signal in Bugsnag, while a single wifi blip stops costing three events.
export const NETWORK_EVENT_WINDOW_MS = 10 * 60 * 1000;

// beforeunload fires when a navigation STARTS — and the navigation can then be cancelled by
// an unsaved-changes prompt, with no pagehide/pageshow to say so. The signal is therefore
// time-boxed (same lesson as session.js's hand-off flash window): long enough to cover a
// slow navigation's fetch teardown, short enough that a surviving page resumes reporting.
export const UNLOAD_SIGNAL_WINDOW_MS = 10 * 1000;

const emptyReasons = () => ({
  handoff: 0,
  unloading: 0,
  hidden: 0,
  offline: 0,
  window: 0,
});

let unloadSignalAt = 0;
let lastSentAt = 0;
let suppressedSinceLastSent = 0;
let suppressedByReason = emptyReasons();

export function isNetworkFailureMessage(message) {
  return NETWORK_MESSAGE_RE.test(String(message || '').trim());
}

// The first explanation wins; the order is the order of certainty (a hand-off in progress
// explains everything, an offline flag is the weakest).
function explainedBy(env) {
  if (env.handoff) return 'handoff';
  if (env.unloading) return 'unloading';
  if (env.visibility === 'hidden') return 'hidden';
  if (env.online === false) return 'offline';
  return null;
}

// fetchM attaches its diagnostics to every fetch() rejection BEFORE the caller's catch runs —
// and some catches (actions/error.js handleError) rewrite err.message to a generic
// "An error occurred" before notifying. The message no longer says "network", but the
// object still does; without this, that whole path would bypass the filter unannotated.
// Only a rejection whose ORIGINAL message was network-shaped counts (networkFailure, decided
// at attach time): fetch() also rejects for a bad request option or an abort, and those are
// real errors that must reach Bugsnag whatever a catch renamed them to.
export function hasNetworkDiagnostics(exception) {
  return !!(exception && typeof exception === 'object'
    && exception.mhubNetwork && typeof exception.mhubNetwork === 'object'
    && exception.mhubNetwork.networkFailure === true);
}

// Pure decision core, exported for tests. `env` carries the browser facts so tests need no
// jsdom fixture: { handoff, unloading, visibility, online, now }. `diagnosed` says fetchM
// already identified the rejection as a network-level failure (see hasNetworkDiagnostics).
// Returns { action: 'pass' } (not network-shaped: send untouched),
//         { action: 'drop' } (explained or inside the rate window),
//         { action: 'send', network } (the one allowed event, with its annotation).
export function decideNetworkNoise(message, env, diagnosed) {
  if (!isNetworkFailureMessage(message) && !diagnosed) {
    return { action: 'pass' };
  }
  // The page can explain the failure — there is nothing to report, but the reason is counted
  // so the next allowed event can say how the silence was spent.
  const explained = explainedBy(env);
  if (explained) {
    suppressedSinceLastSent += 1;
    suppressedByReason[explained] += 1;
    return { action: 'drop' };
  }
  if (env.now - lastSentAt < NETWORK_EVENT_WINDOW_MS) {
    suppressedSinceLastSent += 1;
    suppressedByReason.window += 1;
    return { action: 'drop' };
  }
  lastSentAt = env.now;
  const network = {
    online: env.online,
    visibility: env.visibility,
    suppressedSinceLastSent,
    suppressedByReason,
  };
  suppressedSinceLastSent = 0;
  suppressedByReason = emptyReasons();
  return { action: 'send', network };
}

export function installBugsnagNetworkNoiseFilter(win) {
  const w = win || window;
  const bugsnag = w.Bugsnag;
  if (!bugsnag || typeof bugsnag.notifyException !== 'function') {
    return; // notifier script blocked/absent — nothing to filter
  }
  if (bugsnag.notifyException.networkNoiseFiltered) {
    return; // hot reload re-runs the entry — never double-wrap
  }
  w.addEventListener('beforeunload', () => { unloadSignalAt = Date.now(); }, false);
  w.addEventListener('pagehide', () => { unloadSignalAt = Date.now(); }, false);
  // bfcache restore: the page is live again and its failures are reportable again.
  w.addEventListener('pageshow', () => { unloadSignalAt = 0; }, false);

  const original = bugsnag.notifyException;
  const wrapped = function notifyExceptionFiltered(exception, name, metaData, severity) {
    // v3 signature juggling, mirrored from the notifier: a non-string second argument is
    // the metaData. A string exception delegates to Bugsnag.notify internally — its
    // .message read below is undefined, so it passes through untouched either way.
    let realName = name;
    let realMetaData = metaData;
    if (realName && typeof realName !== 'string') {
      realMetaData = realName;
      realName = undefined;
    }
    const message = exception && typeof exception === 'object'
      ? (exception.message || exception.description)
      : exception;
    const now = Date.now();
    const verdict = decideNetworkNoise(message, {
      handoff: isSessionHandoffInProgress(),
      unloading: unloadSignalAt !== 0 && (now - unloadSignalAt) < UNLOAD_SIGNAL_WINDOW_MS,
      visibility: w.document && w.document.visibilityState,
      online: w.navigator && w.navigator.onLine,
      now,
    }, hasNetworkDiagnostics(exception));
    if (verdict.action === 'drop') {
      return undefined;
    }
    if (verdict.action === 'send') {
      const annotated = realMetaData || {};
      annotated.network = verdict.network;
      if (exception && exception.mhubNetwork && typeof exception.mhubNetwork === 'object') {
        // fetchM's diagnostics: the request's URL and the page state when it died.
        Object.assign(annotated.network, exception.mhubNetwork);
      }
      return original.call(bugsnag, exception, realName, annotated, severity);
    }
    return original.call(bugsnag, exception, name, metaData, severity);
  };
  wrapped.networkNoiseFiltered = true;
  bugsnag.notifyException = wrapped;
}

export function resetNetworkNoiseStateForTests() {
  unloadSignalAt = 0;
  lastSentAt = 0;
  suppressedSinceLastSent = 0;
  suppressedByReason = emptyReasons();
}



// WEBPACK FOOTER //
// ./src/utils/bugsnagNetworkNoise.js